import { useCallback, useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { Switch } from 'shared/ui/switch'
import { Label } from 'shared/ui/label'
import { Badge } from 'shared/ui/badge'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Plus, ShieldCheck, Search, Loader2, Users, User, X } from 'lucide-react'
import { Separator } from 'shared/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { searchWorkflowEmployees, searchWorkflowRoles } from 'modules/admin/api/workflowConfigurationService'
import type { WorkflowStage, WorkflowStatusOption } from 'modules/admin/types/workflowTypes'

interface WorkflowStageSectionProps {
  stages: WorkflowStage[]
  onAddStage: () => void
  onUpdateStage: (stageId: string, changes: Partial<WorkflowStage>) => void
  tenantId: string
  statusOptions: WorkflowStatusOption[]
}

interface LookupOption {
  id: string
  label: string
  description?: string
}

type LookupType = 'role' | 'employee'
const MISSING_APPROVER_OPTIONS = [
  { value: 'FAIL', label: 'Fail' },
  { value: 'SKIP', label: 'Skip' },
] as const

const renderMissingApproverBadge = (value?: string | null) => {
  const normalized = (value ?? 'FAIL').trim().toUpperCase()
  const className =
    normalized === 'SKIP'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-rose-200 bg-rose-50 text-rose-700'

  return (
    <Badge variant="outline" className={className}>
      {normalized}
    </Badge>
  )
}

export function WorkflowStageSection({
  stages,
  onAddStage,
  onUpdateStage,
  tenantId,
  statusOptions,
}: WorkflowStageSectionProps) {
  const [lookupState, setLookupState] = useState<{ stageId: string; type: LookupType } | null>(null)
  const [lookupSearch, setLookupSearch] = useState('')
  const [lookupItems, setLookupItems] = useState<LookupOption[]>([])
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  const handleSeqChange = (stageId: string, nextSeq: number) => {
    const sequences = stages.map((stage) => ({
      id: stage.stageId,
      seqOrder: stage.stageId === stageId ? nextSeq : stage.seqOrder,
    }))
    const sorted = sequences
      .sort((a, b) => a.seqOrder - b.seqOrder)
      .map((entry, index) => ({ ...entry, seqOrder: index + 1 }))
    sorted.forEach((entry) => {
      onUpdateStage(entry.id, { seqOrder: entry.seqOrder })
    })
  }

  const isLookupOpen = lookupState !== null

  const loadLookupOptions = useCallback(
    async (type: LookupType, searchTerm: string) => {
      if (type === 'employee' && !tenantId) {
        setLookupItems([])
        setLookupError('กรุณาเลือก tenant ก่อน')
        return
      }

      setLookupLoading(true)
      setLookupError(null)
      try {
        if (type === 'employee') {
          const response = await searchWorkflowEmployees(tenantId, searchTerm)
          setLookupItems(
            response.items.map((item) => ({
              id: item.employeeId,
              label: item.fullName,
              description: item.position || item.orgName || item.employeeCode || '',
            })),
          )
        } else {
          const response = await searchWorkflowRoles(searchTerm)
          setLookupItems(
            response.items.map((item) => ({
              id: item.roleId,
              label: item.displayName,
              description: item.code || item.description || '',
            })),
          )
        }
      } catch {
        setLookupError('ไม่สามารถโหลดข้อมูลได้')
        setLookupItems([])
      } finally {
        setLookupLoading(false)
      }
    },
    [tenantId],
  )

  useEffect(() => {
    if (!lookupState) {
      return
    }
    const handler = setTimeout(() => {
      loadLookupOptions(lookupState.type, lookupSearch)
    }, 300)
    return () => clearTimeout(handler)
  }, [lookupState, lookupSearch, loadLookupOptions])

  const handleLookupSelect = (option: LookupOption) => {
    if (!lookupState) return
    if (lookupState.type === 'employee') {
      onUpdateStage(lookupState.stageId, { fieldId: option.id, fieldName: option.label })
    } else {
      onUpdateStage(lookupState.stageId, { roleId: option.id, roleName: option.label })
    }
    setLookupState(null)
    setLookupSearch('')
    setLookupItems([])
  }

  const handleClearSelection = (stageId: string, type: LookupType) => {
    if (type === 'employee') {
      onUpdateStage(stageId, { fieldId: '', fieldName: '' })
    } else {
      onUpdateStage(stageId, { roleId: '', roleName: '' })
    }
  }

  const handleStatusChange = (stageId: string, statusCode: string) => {
    if (!statusCode) {
      onUpdateStage(stageId, { statusCode: undefined, statusName: '' })
      return
    }
    const status = statusOptions.find((item) => item.statusCode === statusCode)
    onUpdateStage(stageId, { statusCode, statusName: status?.statusName ?? statusCode })
  }

  return (
    <Card className="border border-gray-200">
      <CardHeader className="flex flex-col space-y-1">
        <CardTitle className="flex items-center justify-between text-base font-semibold text-gray-900">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Stage Designer
          </span>
          <Button variant="outline" size="sm" onClick={onAddStage}>
            <Plus className="mr-1 h-4 w-4" />
            Add Stage
          </Button>
        </CardTitle>
        <p className="text-sm text-gray-500">จัดลำดับ stage และกำหนดบทบาท/ผู้อนุมัติ พร้อมตั้ง Gate stage</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {stages
          .slice()
          .sort((a, b) => a.seqOrder - b.seqOrder)
          .map((stage) => (
            <div key={stage.stageId} className="rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Badge variant="outline">{stage.seqOrder}</Badge>
                    {stage.name}
                    {renderMissingApproverBadge(stage.onMissingApprover)}
                  </div>
                  <p className="text-xs text-gray-500">
                    Status: {stage.statusName || stage.statusCode || 'ยังไม่กำหนด'}
                  </p>
                </div>
                {stage.isGate ? <Badge variant="default">Gate</Badge> : null}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase text-gray-500">Role</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLookupState({ stageId: stage.stageId, type: 'role' })}
                    >
                      <Users className="mr-2 h-4 w-4" />
                      {stage.roleName || (stage.roleId ? 'ไม่พบข้อมูล Role นี้แล้ว' : 'เลือก Role')}
                    </Button>
                    {stage.roleId ? (
                      <Button variant="ghost" size="sm" onClick={() => handleClearSelection(stage.stageId, 'role')}>
                        เคลียร์
                      </Button>
                    ) : null}
                  </div>
                  {stage.roleId && !stage.roleName ? (
                    <p className="text-xs text-red-500">Role นี้ถูกลบ/เปลี่ยน ID ไปแล้ว กรุณาเลือกใหม่ (ID: {stage.roleId})</p>
                  ) : stage.roleId ? (
                    <p className="text-xs text-gray-500">ID: {stage.roleId}</p>
                  ) : null}
                </div>
                <div>
                  <Label className="text-xs uppercase text-gray-500">Stage Code</Label>
                  <Input value={stage.code} onChange={(event) => onUpdateStage(stage.stageId, { code: event.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-gray-500">Permit Status</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={stage.statusCode ?? undefined}
                      onValueChange={(value) => handleStatusChange(stage.stageId, value)}
                      disabled={statusOptions.length === 0}
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue placeholder={statusOptions.length === 0 ? 'ไม่มีสถานะ' : 'เลือกสถานะ'} />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option.statusCode} value={option.statusCode}>
                            {option.statusName} ({option.statusCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {stage.statusCode ? (
                      <Button variant="ghost" size="icon" onClick={() => handleStatusChange(stage.stageId, '')}>
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-gray-500">Field (Employee)</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!tenantId}
                      onClick={() => setLookupState({ stageId: stage.stageId, type: 'employee' })}
                    >
                      <User className="mr-2 h-4 w-4" />
                      {stage.fieldName || (stage.fieldId ? 'ไม่พบข้อมูลพนักงานนี้แล้ว' : 'เลือก Field')}
                    </Button>
                    {stage.fieldId ? (
                      <Button variant="ghost" size="sm" onClick={() => handleClearSelection(stage.stageId, 'employee')}>
                        เคลียร์
                      </Button>
                    ) : null}
                  </div>
                  {stage.fieldId && !stage.fieldName ? (
                    <p className="text-xs text-red-500">พนักงานนี้ถูกลบ/เปลี่ยน ID ไปแล้ว กรุณาเลือกใหม่ (ID: {stage.fieldId})</p>
                  ) : stage.fieldId ? (
                    <p className="text-xs text-gray-500">ID: {stage.fieldId}</p>
                  ) : null}
                  {!tenantId ? <p className="text-xs text-red-500">ต้องเลือก tenant ก่อน</p> : null}
                </div>
                <div>
                  <Label className="text-xs uppercase text-gray-500">Sequence Order</Label>
                  <Input
                    type="number"
                    min={1}
                    value={stage.seqOrder}
                    onChange={(event) => {
                      const nextSeq = Number(event.target.value) || 1
                      handleSeqChange(stage.stageId, nextSeq)
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase text-gray-500">On Missing Approver</Label>
                  <Select
                    value={stage.onMissingApprover ?? 'FAIL'}
                    onValueChange={(value: 'FAIL' | 'SKIP') =>
                      onUpdateStage(stage.stageId, { onMissingApprover: value })
                    }
                  >
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue placeholder="Select behavior" />
                    </SelectTrigger>
                    <SelectContent>
                      {MISSING_APPROVER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">
                    FAIL หยุด workflow และแจ้ง error, SKIP ข้าม stage นี้ไปยังขั้นตอนถัดไป
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2 rounded-md border border-gray-200 p-3">
                <p className="text-xs font-medium text-gray-600">Gate Stage</p>
                <Switch checked={stage.isGate} onCheckedChange={(checked) => onUpdateStage(stage.stageId, { isGate: checked })} />
              </div>
            </div>
          ))}
        {stages.length === 0 ? <p className="text-sm text-gray-500">ยังไม่มี stage</p> : null}
      </CardContent>

      <Dialog open={isLookupOpen} onOpenChange={(open) => (!open ? setLookupState(null) : null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {lookupState?.type === 'employee' ? 'เลือกผู้รับผิดชอบ (Field)' : 'เลือก Role สำหรับ Stage'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                value={lookupSearch}
                onChange={(event) => setLookupSearch(event.target.value)}
                placeholder="พิมพ์เพื่อค้นหา"
              />
            </div>
            <Separator />
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {lookupLoading ? (
                <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  กำลังโหลดข้อมูล...
                </div>
              ) : lookupError ? (
                <p className="py-4 text-sm text-red-500">{lookupError}</p>
              ) : lookupItems.length === 0 ? (
                <p className="py-4 text-sm text-gray-500">ไม่พบข้อมูล</p>
              ) : (
                lookupItems.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleLookupSelect(option)}
                    className="w-full rounded-md border border-gray-200 bg-white p-3 text-left hover:border-primary"
                  >
                    <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                    <p className="text-xs text-gray-500">{option.description || option.id}</p>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLookupState(null)}>
              ปิด
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
